import { useQueryParams } from '@/hooks';
import { useRequestGetEvents } from '@/hooks/httpRequest/events';
import { DataTablePageSizeEnum, EventTypeEnum, EventStatusEnum } from '@/enums';
import { useCallback, useEffect } from 'react';

const ALL_TYPES = [EventTypeEnum.BLIND_DATING];
const ALL_STATUSES = [EventStatusEnum.SCHEDULED, EventStatusEnum.LIVE, EventStatusEnum.ENDED];

const useEvents = () => {
	const { updateSearchParams, urlSearchParams, values: [page, limit] } = useQueryParams(
		['page', 'limit', 'type', 'status', 'startsAtFrom', 'startsAtTo'],
		{ page: 1, limit: DataTablePageSizeEnum._15 },
	);

	useEffect(() => {
		if (!urlSearchParams.has('status') && !urlSearchParams.has('type')) {
			updateSearchParams(prev => {
				ALL_TYPES.forEach(t => prev.append('type', t));
				ALL_STATUSES.forEach(s => prev.append('status', s));
				return prev;
			});
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const type = urlSearchParams.getAll('type') as EventTypeEnum[];
	const status = urlSearchParams.getAll('status') as EventStatusEnum[];
	const startsAtFrom = urlSearchParams.get('startsAtFrom') ?? undefined;
	const startsAtTo = urlSearchParams.get('startsAtTo') ?? undefined;

	const result = useRequestGetEvents({
		status: status.length ? status : undefined,
		type: type.length === 1 ? type[0] : undefined,
		startsAtFrom,
		startsAtTo,
		page: page as number,
		limit: limit as number,
	});

	const handleFilterChange = useCallback((key: string, values: string[]) => {
		updateSearchParams(prev => {
			prev.delete(key);
			values.forEach(v => prev.append(key, v));
			return prev;
		});
	}, [updateSearchParams]);

	const handlePeriodChange = useCallback((from?: string, to?: string) => {
		updateSearchParams(prev => {
			if (from) prev.set('startsAtFrom', from); else prev.delete('startsAtFrom');
			if (to) prev.set('startsAtTo', to); else prev.delete('startsAtTo');
			return prev;
		});
	}, [updateSearchParams]);

	const clearFilters = useCallback(() => {
		updateSearchParams(prev => {
			prev.delete('type');
			prev.delete('status');
			prev.delete('startsAtFrom');
			prev.delete('startsAtTo');
			return prev;
		});
	}, [updateSearchParams]);

	const hasActiveFilters = type.length > 0 || status.length > 0 || !!startsAtFrom || !!startsAtTo;

	return {
		...result,
		activeFilters: { type, status, startsAtFrom, startsAtTo },
		hasActiveFilters,
		handleFilterChange,
		handlePeriodChange,
		clearFilters,
	};
};

export default useEvents;
